import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CSSModules from 'react-css-modules';
import VisibilitySensor from 'react-visibility-sensor';
import find from 'lodash/find';
import debounce from 'lodash/debounce';
import { injectConfig, configShape } from '@client/hoc/config';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import {
    saveImpressionData as saveImpressionDataFromRedux,
    clearImpressionData as clearImpressionDataFromRedux,
} from '@client/redux/modules/tracking/actions';
import {
    getImpressionData
} from '@client/redux/modules/tracking/selectors';
import {
    TRACK_RECO_COLLABORATIVE,
    TRACK_RECO_TOPSELLER
} from '@client/config/constants';
import {
    visibleOnce,
    track,
    makeLoopFunction
} from '@client/helpers';
import { getRecoImpressionData, getTrackingDataProductClick } from '@client/helpers/tracking';
import { getImageUrl } from '@client/components/shared/Helpers';
import Recommendation from '../Recommendation';

import styles from './styles.scss';

export class RecommendationsCarouselMobile extends PureComponent {
    constructor(props) {
        super(props);
        const articleVisibilityFunc = {};
        props.recommendations.forEach((reco, index) => {
            articleVisibilityFunc[reco.sku] = visibleOnce({
                ...reco,
                position: index + 1,
                source: props.source || (reco.reco_tracking_string && TRACK_RECO_COLLABORATIVE) || TRACK_RECO_TOPSELLER
            }, props.saveImpressionData);
        });
        this.state = { articleVisibilityFunc };
        this.handleSwipe = debounce(this.handleSwipe, 200, { leading: false, trailing: true });
    }

    // this is for locking mechanism for auto scrolling to not to fire swipe callback
    scrollEventLock = false;

    handleSwipe = () => {
        if (this.props.onSwipe && !this.scrollEventLock) {
            this.props.onSwipe();
        }
        else if (this.scrollEventLock) {
            this.scrollEventLock = false;
        }
    }

    render() {
        if (!this.props.recommendations.length) {
            return null;
        }

        const { config, newCardStyle } = this.props;

        return (
            <div styleName="wrapper">
                <div styleName="carousel-wrapper" ref={this.carouselRef} onScroll={this.handleSwipe}>
                    {this.props.recommendations.map((reco, index) => (
                        <div
                            key={reco.sku}
                            styleName="carousel-slide"
                        >
                            <VisibilitySensor
                                onChange={this.state.articleVisibilityFunc[reco.sku]}
                                delayedCall={true}
                                intervalDelay={800}
                                containment={this.props.trackByContainer && this.carouselRef && this.carouselRef.current}
                            >
                                <Recommendation
                                    imageUrl={getImageUrl(config, 'zlcatalog', reco.imageUrl)}
                                    campaignIdentifier={reco.campaignIdentifier}
                                    brand={reco.brand}
                                    sku={reco.sku}
                                    price={reco.price}
                                    specialPrice={reco.specialPrice}
                                    similarPrices={reco.similarPrices}
                                    stockStatus={reco.stockStatus}
                                    onClick={this.onRecoClick}
                                    source={this.props.source}
                                    position={index}
                                    newCardStyle={newCardStyle}
                                />
                            </VisibilitySensor>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    componentDidMount() {
        this.startImpressionTracking();
    }

    componentWillUnmount() {
        this.stopImpressionTracking();
    }

    componentDidUpdate(prevProps) {
        // This will scroll to the first element when the recommendations change
        try {
            if (prevProps.recommendations !== this.props.recommendations) {
                this.scrollEventLock = true;
                this.carouselRef.current.scroll(0, 0);
            }
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }

    onRecoClick = (e, sku, recoPosition) => {
        const reco = find(this.props.recommendations, { sku });

        // clear the uncalled visibleOnce callback to prevent unwanted impression
        this.setState({ articleVisibilityFunc: {} });

        if (this.state.blockRedirect) {
            e.preventDefault();
        }

        this.trackRecoClick(reco, recoPosition);
        if (this.props.onRecoClick) {
            this.props.onRecoClick();
        }
    }

    trackRecoClick = (reco, recoPosition) => {
        const { source } = this.props;
        const trackingData = getTrackingDataProductClick(reco, recoPosition, source);
        track('productClick', trackingData);
    }

    startImpressionTracking = () => {
        this.poolTracking = makeLoopFunction(this.impressionTracking, 5000);
        this.poolTracking.start();
    }

    impressionTracking = () => {
        const { config } = this.props;

        if (this.props.impressionData.length > 0) {
            const payload = getRecoImpressionData(this.props.impressionData, undefined, config.appdomain);
            track('eec_impressions', payload);
            this.props.clearImpressionData();
        }
    }

    stopImpressionTracking = () => {
        this.poolTracking.stop();
        // tracking here before leaving
        this.impressionTracking();
        // clear the instance on leaving becasue we need to track whenever users come back here
        this.setState({ articleVisibilityFunc: {} });
    }

    carouselRef = React.createRef();
}

RecommendationsCarouselMobile.propTypes = {
    recommendations: PropTypes.array.isRequired,
    source: PropTypes.number,
    withoutHeader: PropTypes.bool,
    saveImpressionData: PropTypes.func.isRequired,
    clearImpressionData: PropTypes.func.isRequired,
    impressionData: PropTypes.array.isRequired,
    trackByContainer: PropTypes.bool,
    deviceWidth: PropTypes.number.isRequired,
    config: configShape,
    newCardStyle: PropTypes.bool,
    onRecoClick: PropTypes.func,
    onSwipe: PropTypes.func
};

const mapStateToProps = (state) => ({
    impressionData: getImpressionData(state),
    deviceWidth: getClientDeviceWidth(state)
});

const mapDispatchToProps = {
    saveImpressionData: saveImpressionDataFromRedux,
    clearImpressionData: clearImpressionDataFromRedux
};

export default injectConfig(
    connect(mapStateToProps, mapDispatchToProps)(CSSModules(RecommendationsCarouselMobile, styles))
);
